import { isValid, parse } from "date-fns";
import { id } from "date-fns/locale/id";
import React, { forwardRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { useAuthStore } from "../stores/authStores";

interface CustomDateInputProps {
  value?: string;
  onClick?: () => void;
  onManualChange?: (val: string, date: Date | null) => void;
}

const CustomDateInput = forwardRef<HTMLInputElement, CustomDateInputProps>(
  ({ value, onClick, onManualChange }, ref) => {
    const [internalValue, setInternalValue] = useState(value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 8);

      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }

      setInternalValue(formatted);

      if (digits.length === 8) {
        const parsed = parse(formatted, "dd/MM/yyyy", new Date());
        onManualChange?.(formatted, isValid(parsed) ? parsed : null);
      } else {
        onManualChange?.(formatted, null);
      }
    };

    // Sync ketika user pilih dari kalender
    React.useEffect(() => {
      if (value) setInternalValue(value);
    }, [value]);

    return (
      <input
        ref={ref}
        value={internalValue}
        onClick={onClick}
        onChange={handleChange}
        placeholder="DD/MM/YYYY"
        inputMode="numeric"
        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-morning-300"
      />
    );
  },
);

CustomDateInput.displayName = "CustomDateInput";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);
  const setUser = useAuthStore((s) => s.setUser);

  const [nik, setNik] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayValue, setDisplayValue] = useState("");
  const [loading, setLoading] = useState(false);

  const toApiFormat = (date: Date): string => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  const handleCalendarChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      const d = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      setDisplayValue(`${d}/${m}/${y}`);
    } else {
      setDisplayValue("");
    }
  };

  const handleManualChange = (formatted: string, date: Date | null) => {
    setSelectedDate(date);
  };

  const handleLogin = async () => {
    if (!selectedDate) {
      alert("Harap pilih tanggal lahir");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post("/login", {
        nik,
        birth_date: toApiFormat(selectedDate),
      });

      const { token } = res.data;

      localStorage.setItem("auth_token", token);
      setToken(token);

      // langsung ambil user asli
      const me = await axios.get("me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUser(me.data.data);

      navigate("/home");
    } catch (error: any) {
      console.error("Login gagal", error);

      if (error.response) {
        alert(error.response.data.message || "Login gagal");
      } else {
        alert("Server tidak merespon");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#D8F3F7" }}
    >
      <div className="bg-morning-300 text-white text-center py-12 px-6">
        <h1 className="text-2xl font-bold mb-2">Selamat Datang 👋</h1>
        <p className="text-sm opacity-90">
          Masuk untuk melanjutkan konsultasi kesehatan Anda
        </p>
        <img
          src="https://m.linksehat.com/assets/img/icons/ask-doctor-white.png"
          alt="Login"
          className="w-28 mx-auto mt-6"
        />
      </div>

      <div className="flex-1 bg-white rounded-t-3xl -mt-6 px-6 pt-8 pb-12 shadow-lg">
        <h2 className="text-lg font-semibold text-gray-700 mb-6 text-center">
          Login Akun
        </h2>

        <div className="mb-4">
          <label className="text-sm text-gray-600 mb-1 block">NIK</label>
          <input
            type="text"
            value={nik}
            onChange={(e) => setNik(e.target.value)}
            placeholder="Masukkan NIK"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-morning-300"
          />
        </div>

        <div className="mb-6">
          <label className="text-sm text-gray-600 mb-1 block">
            Tanggal Lahir
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={handleCalendarChange}
            dateFormat="dd/MM/yyyy"
            locale={id}
            showYearDropdown
            showMonthDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={80}
            maxDate={new Date()}
            wrapperClassName="w-full"
            popperPlacement="bottom-start"
            customInput={
              <CustomDateInput
                value={displayValue}
                onManualChange={handleManualChange}
              />
            }
          />
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-morning-300 text-white font-semibold shadow-md hover:bg-morning-400 transition duration-200 disabled:opacity-50"
        >
          {loading ? "Memproses..." : "Masuk"}
        </button>
      </div>
    </div>
  );
};

export default Login;
